import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { printerStore } from "../../stores/print_mapping/printer_store"
import { areaStore } from "../../stores/locales/area_store"
import { getCsrfToken } from "shared/form_helpers"

/**
 * Manages SVG connection lines between the selected printer and mapped workstations
 * on the visual print mapper floor plan.
 *
 * - Draws dashed SVG path elements from printer icon center to workstation icon centers
 * - Layers SVG above floor plan image and below device icons (z-index management via HTML)
 * - Sources the current workstations (with their print_mappings) directly from the
 *   print mappings API on area/printer change, so it no longer depends on the retired
 *   temporary `print-mapper--visual-print-mapper` controller.
 * - Listens for bubbling custom events emitted by the floor plan icon manager and the
 *   print-class-toggle-panel:
 *   - `floorplan:mapping-created` (detail: { workstationId, workstation }): upsert the
 *     workstation locally and add the single line to the newly mapped workstation
 *     (300ms fade-in) if it maps to the current printer — no full-area refetch.
 *     Dispatched by the print-class-toggle-panel after a successful create.
 *   - `floorplan:mapping-deleted` (detail: { deviceId, mappingId, workstation }): upsert
 *     the workstation locally, then keep/redraw or remove the single line (300ms
 *     fade-out) based on whether it still maps to the current printer (preserving
 *     multi-mapping-to-same-printer semantics) — no full-area refetch. Dispatched by the
 *     icon manager's mapping card.
 * - Clears all lines when printer selection or area changes
 * - Repositions line endpoints on floor plan container resize via ResizeObserver
 *
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5, 11.6, 11.7, 11.8
 */
export default class extends Controller {
    static stores = [printerStore, areaStore]
    static targets = ["svg"]

    connect() {
        // Initialize instance state BEFORE useStore(this). useStore synchronously
        // invokes the store callbacks (onPrinterStoreUpdate / onAreaStoreUpdate) with
        // the stores' current values during connect(), and those callbacks read
        // this._workstations / this._currentPrinter — so they must exist first, or
        // onPrinterStoreUpdate throws "can't access property 'length', this._workstations
        // is undefined" and the controller fails to connect.
        this._currentPrinter = null
        this._workstations = []
        this._workstationsRequestId = 0
        // Monotonic token identifying the CURRENT draw pass. Every event that
        // restarts drawing (printer change, workstation reload, visibility toggle,
        // resize-driven redraw) bumps this. Deferred/retried draw callbacks capture
        // the generation they were queued under and abort if it has since advanced,
        // so stale work from a previous RAPID selection cannot append a line after a
        // newer selection has already cleared and redrawn. This is what makes rapid
        // A -> B -> A toggling deterministic (previously stale retry timers raced the
        // final clear, leaving no line).
        this._drawGeneration = 0
        this._isDrawing = false
        // Set of device/workstation ids whose icons are currently hidden by the
        // "Hide devices with mappings" toggle. Lines to these workstations must not be
        // drawn (and existing ones are removed) so a hidden device shows no line.
        this._hiddenMappedIds = new Set()

        useStore(this)

        // Bind event handlers for custom events emitted by the icon manager and the
        // print-class-toggle-panel. Both events bubble up to the shared root element.
        this._onMappingCreated = this._handleMappingCreated.bind(this)
        this._onMappingDeleted = this._handleMappingDeleted.bind(this)
        this._onMappedVisibilityChanged = this._handleMappedVisibilityChanged.bind(this)

        this.element.addEventListener('floorplan:mapping-created', this._onMappingCreated)
        this.element.addEventListener('floorplan:mapping-deleted', this._onMappingDeleted)
        this.element.addEventListener('floorplan:mapped-visibility-changed', this._onMappedVisibilityChanged)

        // Set up ResizeObserver on the floor-plan reference box to reposition endpoints
        // on genuine layout changes (window resize, floor plan image load). We observe
        // the reference container (.img-container) rather than the SVG's parent, and we
        // DEBOUNCE + GUARD the callback. This matters because _syncSvgViewBox() mutates
        // the SVG's own width/height/left/top on every line draw; if the observer
        // reacted to those self-inflicted size changes it would call _redrawAllLines(),
        // which wipes ALL lines (svg.innerHTML = '') and redraws from scratch. When a
        // printer maps to several workstations drawn across multiple frames (some via
        // the retry path), that wipe-and-redraw storm left only the last pass's line(s)
        // on screen — which is why multiple lines did not show.
        this._redrawScheduled = false
        this._resizeObserver = new ResizeObserver(() => {
            // Ignore observer notifications caused by our own SVG resizing mid-draw.
            if (this._isDrawing) return
            // Debounce to a single redraw per frame.
            if (this._redrawScheduled) return
            this._redrawScheduled = true
            requestAnimationFrame(() => {
                this._redrawScheduled = false
                this._redrawAllLines()
            })
        })
        const refContainer = this._referenceContainer()
        if (refContainer) this._resizeObserver.observe(refContainer)
    }

    disconnect() {
        this.element.removeEventListener('floorplan:mapping-created', this._onMappingCreated)
        this.element.removeEventListener('floorplan:mapping-deleted', this._onMappingDeleted)
        this.element.removeEventListener('floorplan:mapped-visibility-changed', this._onMappedVisibilityChanged)

        if (this._resizeObserver) {
            this._resizeObserver.disconnect()
            this._resizeObserver = null
        }
    }

    // =========================================================================
    // STORE CALLBACKS
    // =========================================================================

    onAreaStoreUpdate(area) {
        this._clearAllLines()
        this._workstations = []
        // Reload the workstation list (with their print_mappings) for the new area so
        // lines can be drawn once a printer is selected. Invalidates any in-flight fetch.
        this._loadWorkstations(area)
    }

    onPrinterStoreUpdate(printer) {
        this._currentPrinter = (printer && printer.id) ? printer : null
        // Invalidate any in-flight draw work from a previous selection. Under rapid
        // toggling several draw passes can be queued at once (each schedules an rAF
        // and the retry path bounces at rAF + setTimeout 100ms); without this bump a
        // stale pass could append a line AFTER this newer selection cleared, or clear
        // a line this newer pass just drew. Capturing the generation lets every
        // deferred callback abort unless it is still the current pass.
        const generation = ++this._drawGeneration
        this._clearAllLines()

        // Defer drawing to the next frame. This callback fires SYNCHRONOUSLY from
        // setPrinterStoreValue, in store-subscriber registration order. This controller
        // is registered before print-mapper--printer-selection, so at this instant the
        // NEW printer's icon has not been rendered onto the floor plan yet and the OLD
        // printer's icon may still be present. Drawing now would anchor the line to the
        // stale/old printer position ("the line connects to where the second printer
        // icon was"). requestAnimationFrame lets printer_selection remove the old icon
        // and render the new one first, so _getPrinterCenter resolves the correct icon.
        if (this._currentPrinter && this._workstations.length > 0) {
            requestAnimationFrame(() => {
                // Abort if a newer selection superseded this one before the frame ran.
                if (generation !== this._drawGeneration) return
                if (this._currentPrinter && this._workstations.length > 0) {
                    this._drawLinesForCurrentPrinter(generation)
                }
            })
        }
    }

    // =========================================================================
    // WORKSTATION DATA
    // =========================================================================

    /**
     * Fetch the workstations (with print_mappings) for the given area directly from
     * the print mappings API. This replaces the retired
     * `print-mapper--visual-print-mapper:workstationsRendered` event as the source of
     * workstation data. Uses the same endpoint and CSRF pattern as the icon manager's
     * mapping card loader.
     */
    async _loadWorkstations(area) {
        const areaId = area?.id
        const requestId = ++this._workstationsRequestId

        if (!areaId) {
            this._workstations = []
            return
        }

        try {
            const url = `/api/print_mappings/workstations?area_id=${encodeURIComponent(areaId)}`
            const response = await fetch(url, {
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }

            const data = await response.json()

            // Ignore stale responses if the area changed again while this was in flight.
            if (requestId !== this._workstationsRequestId) return

            this._workstations = (data && data.workstations) || []

            // If a printer is already selected, draw its lines now that data is present.
            if (this._currentPrinter) {
                this._clearAllLines()
                this._drawLinesForCurrentPrinter()
            }
        } catch (error) {
            if (requestId !== this._workstationsRequestId) return
            console.error('print-mapper--connection-lines: failed to load workstations', error)
            this._workstations = []
        }
    }

    // =========================================================================
    // EVENT HANDLERS
    // =========================================================================

    _handleMappingCreated(event) {
        const detail = event.detail || {}
        const workstation = detail.workstation
        const workstationId = detail.workstationId || (workstation && workstation.id)
        if (!workstationId || !this._currentPrinter) return

        // Preferred (fast) path: the create response already carried the fully-resolved
        // workstation (with its updated print_mappings). Upsert it into the local
        // _workstations list — which keeps a later printer toggle correct, because
        // toggles re-derive lines from _workstations — and then draw ONLY this one line
        // locally if the workstation maps to the current printer. No full-area refetch.
        if (workstation && workstation.id) {
            this._upsertWorkstation(workstation)
            if (this._workstationMapsToCurrentPrinter(workstation)) {
                this._drawLineToWorkstation(workstationId)
            }
            return
        }

        // Fallback (no workstation payload on the event): draw the single line for the
        // just-created mapping using the current printer. We deliberately do NOT
        // reintroduce a full-area refetch here (that was the regression). This mirrors
        // the baseline single-targeted behavior; a subsequent area load refreshes
        // _workstations if a toggle needs it.
        this._drawLineToWorkstation(workstationId)
    }

    _handleMappingDeleted(event) {
        // The icon manager's mapping card emits { deviceId, mappingId, workstation }. A
        // workstation can have several mappings to the SAME printer (one per print
        // class), so deleting ONE mapping must NOT necessarily remove the connection
        // line — the line should only disappear once no mapping to the currently-selected
        // printer remains. The delete response carries the updated workstation (with its
        // REMAINING print_mappings), which is the source of truth for that decision.
        const detail = event.detail || {}
        const workstation = detail.workstation
        const workstationId = detail.deviceId || detail.workstationId || (workstation && workstation.id)
        if (!workstationId) return

        // Preferred (fast) path: upsert the updated workstation locally, then re-evaluate
        // ONLY this workstation against the current printer. If it still maps to the
        // current printer (another mapping to the same printer remains), keep/redraw the
        // line; otherwise remove it. This preserves multi-mapping-to-same-printer
        // semantics (clause 3.4) without any full-area refetch.
        if (workstation && workstation.id) {
            this._upsertWorkstation(workstation)
            if (this._currentPrinter && this._workstationMapsToCurrentPrinter(workstation)) {
                // Redraw if needed; _drawLineToWorkstation is a no-op when the line
                // already exists, so this safely keeps an existing line in place.
                this._drawLineToWorkstation(workstationId)
            } else {
                this._removeLineToWorkstation(workstationId)
            }
            return
        }

        // Fallback (no workstation payload on the event): remove the single line for
        // this workstation. We do NOT reintroduce a full-area refetch on this event.
        this._removeLineToWorkstation(workstationId)
    }

    /**
     * Upsert a single workstation into this._workstations: replace the existing entry
     * with the same id, or append it if not present. Keeps the local workstation list
     * (the source printer toggles re-derive lines from) in sync with the create/delete
     * response payload without a full-area refetch.
     */
    _upsertWorkstation(workstation) {
        if (!workstation || !workstation.id) return
        const index = this._workstations.findIndex(ws => ws.id === workstation.id)
        if (index === -1) {
            this._workstations.push(workstation)
        } else {
            this._workstations[index] = workstation
        }
    }

    /**
     * Whether the given workstation has at least one print mapping to the currently
     * selected printer. Uses the same source of truth (print_mappings[].printer_id)
     * as _drawLinesForCurrentPrinter.
     */
    _workstationMapsToCurrentPrinter(workstation) {
        if (!this._currentPrinter || !workstation) return false
        return (workstation.print_mappings || []).some(
            m => m.printer_id === this._currentPrinter.id
        )
    }

    /**
     * React to the icon manager's "Hide devices with mappings" toggle. The manager
     * emits floorplan:mapped-visibility-changed with { hidden, mappedDeviceIds }. When
     * hiding, we record the hidden set and remove any lines terminating on those
     * (now-hidden) workstation icons. When un-hiding, we clear the set and redraw so
     * lines to the re-shown workstations return.
     */
    _handleMappedVisibilityChanged(event) {
        const detail = event.detail || {}
        const ids = Array.isArray(detail.mappedDeviceIds) ? detail.mappedDeviceIds : []

        if (detail.hidden) {
            this._hiddenMappedIds = new Set(ids)
            // Remove existing lines to any now-hidden workstation.
            this._hiddenMappedIds.forEach(id => this._removeLineToWorkstation(id))
        } else {
            this._hiddenMappedIds = new Set()
            // Redraw so lines to the re-shown workstations reappear.
            this._redrawAllLines()
        }
    }

    // =========================================================================
    // DRAWING
    // =========================================================================

    _drawLinesForCurrentPrinter(generation = this._drawGeneration) {
        if (!this._currentPrinter || !this.hasSvgTarget) return
        // Abort if a newer selection has superseded this pass.
        if (generation !== this._drawGeneration) return

        // Suppress ResizeObserver-driven redraws while we draw this batch. Appending
        // paths and resizing the SVG (via _syncSvgViewBox) would otherwise notify the
        // observer, which wipes and redraws all lines — dropping lines that are still
        // pending via the retry path. Cleared on the next frame once layout settles.
        this._isDrawing = true

        this._workstations.forEach(ws => {
            const hasMappingToCurrentPrinter = (ws.print_mappings || []).some(
                m => m.printer_id === this._currentPrinter.id
            )

            // Skip workstations whose icons are hidden by the "Hide devices with
            // mappings" toggle — no icon means no anchor point, and the user has
            // explicitly hidden them.
            if (hasMappingToCurrentPrinter && !this._hiddenMappedIds.has(ws.id)) {
                this._drawLineToWorkstation(ws.id, 0, generation)
            }
        })

        // Release the guard after the retry frames have had a chance to run. The retry
        // path bounces at rAF (attempt 1) and setTimeout 100ms (attempt 2), so clear
        // slightly after that window so late-rendered workstation icons still draw
        // without a redraw storm wiping the earlier lines.
        setTimeout(() => {
            // Only release the guard if this is still the current pass. A newer
            // selection owns _isDrawing otherwise, and must not have it cleared out
            // from under it by this stale timer.
            if (generation === this._drawGeneration) this._isDrawing = false
        }, 150)
    }

    _drawLineToWorkstation(workstationId, attempt = 0, generation = this._drawGeneration) {
        if (!this.hasSvgTarget || !this._currentPrinter) return
        // Abort a stale draw/retry: if a newer selection has advanced the draw
        // generation, this call belongs to a superseded pass and must not append a
        // line (that stray append after the newer clear is what left the line
        // missing on rapid toggle-back).
        if (generation !== this._drawGeneration) return

        // Never draw a line to a workstation hidden by the "Hide devices with mappings"
        // toggle. Guards every entry point (draw loop, mapping-created rAF, retries).
        if (this._hiddenMappedIds && this._hiddenMappedIds.has(workstationId)) return

        // Ensure the SVG user coordinate space matches CSS pixels 1:1. Without an
        // explicit width/height + viewBox, an SVG sized with CSS percentages defaults
        // to a 300x150 user space, so pixel path coordinates (e.g. "M 40 40 L 120 80")
        // are scaled/clipped and the line renders off-screen (appears to not show).
        this._syncSvgViewBox()

        // Don't duplicate existing lines
        const existingLine = this.svgTarget.querySelector(`[data-workstation-id="${CSS.escape(workstationId)}"]`)
        if (existingLine) return

        const printerCenter = this._getPrinterCenter()
        const wsCenter = this._getWorkstationCenter(workstationId)

        // TIMING: the selected printer icon and workstation icons are rendered
        // asynchronously (printer_selection_controller / floor plan icon manager). If
        // either isn't in the DOM yet, retry once via rAF and once via a short timeout
        // so the freshly-rendered icons can be found. Bounded — no busy loop.
        if (!printerCenter || !wsCenter) {
            if (attempt === 0) {
                requestAnimationFrame(() => this._drawLineToWorkstation(workstationId, 1, generation))
            } else if (attempt === 1) {
                setTimeout(() => this._drawLineToWorkstation(workstationId, 2, generation), 100)
            }
            return
        }

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        const d = `M ${printerCenter.x} ${printerCenter.y} L ${wsCenter.x} ${wsCenter.y}`
        path.setAttribute('d', d)
        path.setAttribute('stroke', '#ffc107')
        path.setAttribute('stroke-width', '5')
        path.setAttribute('stroke-dasharray', '6 4')
        path.setAttribute('fill', 'none')
        path.setAttribute('data-workstation-id', workstationId)
        path.style.opacity = '0'
        path.style.transition = 'opacity 300ms ease-in'

        this.svgTarget.appendChild(path)

        // Animate in within 300ms
        requestAnimationFrame(() => { path.style.opacity = '0.8' })
    }

    _removeLineToWorkstation(workstationId) {
        if (!this.hasSvgTarget) return

        // Remove EVERY path for this workstation, not just the first. Under CI timing
        // the draw retries (rAF + setTimeout) and multiple draw passes can append more
        // than one <path data-workstation-id="X"> for the same workstation; a single
        // querySelector would leave a duplicate behind, so the line never fully clears
        // on hide. querySelectorAll + forEach guarantees all are removed.
        const lines = this.svgTarget.querySelectorAll(`[data-workstation-id="${CSS.escape(workstationId)}"]`)
        lines.forEach((line) => {
            line.style.transition = 'opacity 300ms ease-out'
            line.style.opacity = '0'
            setTimeout(() => line.remove(), 300)
        })
    }

    _clearAllLines() {
        if (!this.hasSvgTarget) return
        this.svgTarget.innerHTML = ''
    }

    _redrawAllLines() {
        // Bump the generation so any retries still pending from the pass being
        // redrawn abort instead of appending onto this fresh redraw.
        const generation = ++this._drawGeneration
        this._syncSvgViewBox()
        this._clearAllLines()
        if (this._currentPrinter && this._workstations.length > 0) {
            this._drawLinesForCurrentPrinter(generation)
        }
    }

    /**
     * Size the SVG's user coordinate space to match its rendered pixel box so that
     * path coordinates expressed in container pixels map 1:1. Sets width/height
     * attributes and a matching viewBox with preserveAspectRatio="none". No-op if the
     * SVG or its container has no measurable size yet.
     */
    _syncSvgViewBox() {
        if (!this.hasSvgTarget) return

        // Size and position the SVG to exactly overlay the floor-plan box that the
        // icons are positioned against (see _referenceContainer). The SVG's own
        // immediate parent (the deviceList div) collapses to height 0 and sits at the
        // bottom of the floor-plan box, so a CSS width/height:100% + top/left:0 on the
        // SVG does NOT line up with the icons (it produced negative path coordinates).
        // Instead, explicitly place the SVG over the reference box and give it a
        // matching pixel viewBox so path coordinates (also computed against that box)
        // map 1:1.
        const container = this._referenceContainer()
        if (!container) return

        const svgParent = this.svgTarget.parentElement
        if (!svgParent) return

        const containerRect = container.getBoundingClientRect()
        const parentRect = svgParent.getBoundingClientRect()
        const width = Math.round(containerRect.width)
        const height = Math.round(containerRect.height)
        if (width <= 0 || height <= 0) return

        // Position the SVG (absolute, inside svgParent) so its top-left aligns with
        // the reference box's top-left, regardless of where svgParent itself sits.
        this.svgTarget.style.position = 'absolute'
        this.svgTarget.style.left = `${containerRect.left - parentRect.left}px`
        this.svgTarget.style.top = `${containerRect.top - parentRect.top}px`
        this.svgTarget.style.width = `${width}px`
        this.svgTarget.style.height = `${height}px`
        this.svgTarget.style.pointerEvents = 'none'

        this.svgTarget.setAttribute('width', width)
        this.svgTarget.setAttribute('height', height)
        this.svgTarget.setAttribute('viewBox', `0 0 ${width} ${height}`)
        this.svgTarget.setAttribute('preserveAspectRatio', 'none')
    }

    /**
     * The DOM box that the floor-plan icons are positioned against — the single
     * shared coordinate reference for both the SVG overlay and the icon-center math.
     *
     * Icons (workstation icons and the selected printer icon) are appended into the
     * deviceList container with absolute top/left in pixels. deviceList has no CSS
     * `position`, so those coordinates resolve against the nearest positioned
     * ancestor: the `.img-container` floor-plan box (position: relative). We therefore
     * use that box as the origin for everything, which keeps line endpoints aligned
     * with the icons.
     *
     * @returns {HTMLElement|null}
     */
    _referenceContainer() {
        if (!this.hasSvgTarget) return null
        return this.svgTarget.closest('.img-container') || this.svgTarget.parentElement
    }

    /**
     * Resolve the reference box (as a DOMRect) against which icon centers are
     * computed — the floor-plan box shared with the icons (see _referenceContainer).
     *
     * @returns {DOMRect|null}
     */
    _referenceRect() {
        const container = this._referenceContainer()
        return container ? container.getBoundingClientRect() : null
    }

    // =========================================================================
    // POSITIONING
    // =========================================================================

    _getPrinterCenter() {
        // printer_selection_controller renders EVERY printer in the area as a
        // div[data-printer-icon="true"][data-printer-id="<id>"] appended into the
        // deviceList container, and marks the selected one with
        // data-selected-printer="true" for highlighting.
        //
        // Resolve the anchor by the CURRENT printer's id among ALL printer icons
        // (data-printer-id), NOT by the data-selected-printer marker. All printer
        // icons are always present in the DOM, so this is independent of the timing
        // of when the highlight marker is applied — which is what previously broke
        // line redraws when toggling between printers (the connection-lines store
        // callback can fire before the highlight marker is set). Fall back to the
        // selected marker, then any printer icon, for safety.
        let printerIcon = null
        if (this._currentPrinter && this._currentPrinter.id) {
            const id = CSS.escape(this._currentPrinter.id)
            printerIcon =
                this.element.querySelector(`[data-printer-icon="true"][data-printer-id="${id}"]`) ||
                this.element.querySelector(`[data-selected-printer="true"][data-printer-id="${id}"]`)
        }
        if (!printerIcon) {
            printerIcon = this.element.querySelector('[data-selected-printer="true"]')
        }
        if (!printerIcon) return null

        // Compute the center relative to the SVG's own rendered box so the line
        // coordinates share the SVG user-space origin set by _syncSvgViewBox.
        const containerRect = this._referenceRect()
        if (!containerRect) return null

        const iconRect = printerIcon.getBoundingClientRect()

        return {
            x: iconRect.left - containerRect.left + iconRect.width / 2,
            y: iconRect.top - containerRect.top + iconRect.height / 2
        }
    }

    _getWorkstationCenter(workstationId) {
        // Workstation icons are rendered by the floor plan icon manager as
        // div[data-icon-type][data-device-id="<workstationId>"] inside the deviceList
        // container. They have no `.workstation-icon` class and use `data-device-id`.
        // Scope the query to the deviceList container (the SVG's parent) to avoid
        // matching unrelated elements. Workstations are individual devices, so restrict
        // to data-icon-type="device" (a bundle icon also carries data-device-id).
        // Query the workstation icon anywhere within this controller's element
        // (the deviceList container is where the icon manager renders it).
        const wsIcon = this.element.querySelector(
            `[data-icon-type="device"][data-device-id="${CSS.escape(workstationId)}"]`
        )
        if (!wsIcon) return null

        // Compute the center relative to the SVG's own rendered box (same origin as
        // the printer center and the SVG user space).
        const containerRect = this._referenceRect()
        if (!containerRect) return null

        const iconRect = wsIcon.getBoundingClientRect()

        return {
            x: iconRect.left - containerRect.left + iconRect.width / 2,
            y: iconRect.top - containerRect.top + iconRect.height / 2
        }
    }
}
